"use client";

import Link from "next/link";
import Image from "next/image";
import { Search, Menu, Loader2, Sparkles } from "lucide-react";
import { useState, useEffect, useRef, useCallback, useTransition } from "react";
import { usePathname, useRouter } from "next/navigation";
import { ThemeToggle } from "./ThemeToggle";

interface SearchResult {
  url: string;
  title: string;
  excerpt: string;
  category?: string;
}

const FILTER_TAGS = [
  { label: "指南", value: "guide" },
  { label: "FAQ", value: "faq" },
];

export function Header({
  onMenuClick,
  floating = true,
  inFlow = false,
}: {
  onMenuClick?: () => void;
  floating?: boolean;
  inFlow?: boolean;
}) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [pendingFilter, setPendingFilter] = useState<string | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout>();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const [isFilterPending, startFilterTransition] = useTransition();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const isAgentPage = pathname?.startsWith("/agent");
  const isHomePage = pathname === "/";

  const handleCloseSearch = useCallback(() => {
    setIsSearchFocused(false);
    setQuery("");
    setResults([]);
    setSelectedIndex(-1);
    setActiveFilter(null);
  }, []);

  const navigateToSearchResult = useCallback(
    (url: string) => {
      handleCloseSearch();
      router.push(url, { scroll: false });
    },
    [handleCloseSearch, router]
  );

  useEffect(() => {
    if (!isFilterPending) {
      setPendingFilter(null);
    }
  }, [isFilterPending]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 12);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("recentSearches");
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch {}
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsSearchFocused(true);
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
      if (e.key === "Escape" && isSearchFocused) {
        handleCloseSearch();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleCloseSearch, isSearchFocused]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        let filteredResults = data.results || [];
        if (activeFilter) {
          filteredResults = filteredResults.filter((r: SearchResult) => {
            if (activeFilter === "guide") return r.url.includes("/guide") || r.url.includes("/docs/getting-started");
            if (activeFilter === "faq") return r.url.includes("/faq");
            return true;
          });
        }
        setResults(filteredResults);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    }, 150);

    return () => clearTimeout(searchTimeout.current);
  }, [query, activeFilter]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      const selected = results[selectedIndex];
      if (selected) {
        saveRecentSearch(query);
        navigateToSearchResult(selected.url);
      }
    }
  };

  const saveRecentSearch = (searchQuery: string) => {
    if (!searchQuery.trim()) return;
    const updated = [searchQuery, ...recentSearches.filter((s) => s !== searchQuery)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  useEffect(() => {
    if (isSearchFocused) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isSearchFocused]);

  useEffect(() => {
    setIsSearchFocused(false);
    setQuery("");
    setActiveFilter(null);
  }, [pathname]);

  const showDropdown = isSearchFocused && (query.trim() !== "" || recentSearches.length > 0);

  return (
    <>
      {floating ? (
        <div className="h-[5.5rem] sm:h-[5.75rem]" />
      ) : !inFlow ? (
        <div className="h-[4.2rem]" />
      ) : null}
      <header
        className={
          floating
            ? "fixed inset-x-0 top-0 z-50 w-full px-3 pt-3 sm:px-4"
            : inFlow
              ? `relative z-30 w-full border-b ${
                  isAgentPage
                    ? "border-[#dbe4ff]/80 bg-[#f8fbff] dark:border-white/10 dark:bg-[#04060b]"
                    : "border-[#dbe4ff]/70 bg-[#f8fbff]/92 dark:border-white/5 dark:bg-[#04060b]/92"
                }`
            : `fixed inset-x-0 top-0 z-50 w-full backdrop-blur-[20px] ${
                isHomePage
                  ? "border-b border-[#dbe4ff]/70 bg-[#f8fbff]/78 dark:border-white/5 dark:bg-[#04060b]/72"
                  : "bg-[#f8fbff]/90 dark:bg-[#04060b]/90"
              }`
        }
      >
        <div
          className={
            floating
              ? `relative mx-auto max-w-7xl rounded-[1.75rem] border transition-all duration-300 ${
                  isScrolled
                    ? "border-white/80 bg-[rgba(248,250,255,0.78)] shadow-[0_22px_50px_rgba(15,23,42,0.12),0_4px_18px_rgba(51,90,255,0.08)] backdrop-blur-[28px] dark:bg-[rgba(15,23,42,0.78)] dark:border-white/10 dark:shadow-none"
                    : "border-white/65 bg-[rgba(248,250,255,0.72)] shadow-[0_14px_38px_rgba(15,23,42,0.07)] backdrop-blur-[24px] dark:bg-[rgba(15,23,42,0.72)] dark:border-white/5 dark:shadow-none"
                }`
              : inFlow
                ? `mx-auto w-full max-w-[1600px] ${
                    isAgentPage ? "px-3 md:px-4" : "max-w-[1440px]"
                  }`
              : `mx-auto w-full max-w-[1440px] transition-all duration-300 ${
                  isHomePage
                    ? isScrolled
                      ? "border-b border-[#dbe4ff]/80 bg-[#f8fbff]/88 shadow-[0_14px_32px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-[#04060b]/84 dark:shadow-[0_12px_24px_rgba(0,0,0,0.22)]"
                      : "border-b border-[#dbe4ff]/60 bg-[#f8fbff]/74 dark:border-white/5 dark:bg-[#04060b]/70"
                    : ""
                }`
          }
        >
          <div
            className={
              floating
                ? "flex h-[4.2rem] items-center px-4 md:px-6 lg:px-7"
                : inFlow && isAgentPage
                  ? "flex h-[4.2rem] items-center px-0"
                  : "flex h-[4.2rem] items-center px-3 md:px-4 xl:px-6"
            }
          >
            <button
              className="mr-4 -ml-2 rounded-xl p-2.5 transition-colors hover:bg-[#f5f5f7]/60 dark:hover:bg-white/10 lg:hidden"
              onClick={onMenuClick}
              aria-label="Toggle Menu"
            >
              <Menu className="h-5 w-5 text-[#1d1d1f] dark:text-white" />
            </button>

            <Link href="/" className="group mr-5 flex flex-shrink-0 items-center gap-3.5">
              <div className="relative rounded-[0.9rem] bg-white/72 dark:bg-white/10 p-1.5 shadow-[0_8px_22px_rgba(15,23,42,0.07)] dark:shadow-none">
                <Image
                  src="/logo.png"
                  alt="韦尼克 Logo"
                  width={112}
                  height={28}
                  className="h-7 w-auto rounded-sm object-contain"
                  priority
                />
              </div>
              <div className="hidden sm:block">
                <div className="text-base font-semibold tracking-tight text-[#1d1d1f] dark:text-white transition-colors group-hover:text-[#406EFF] dark:group-hover:text-[#bfd0ff]">
                  韦尼克平台
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#94a3b8] dark:text-[#64748b]">
                  AI Document Platform
                </div>
              </div>
            </Link>

            <div className="flex flex-1 justify-center md:ml-4 md:justify-start">
              <div
                className={`relative transition-all duration-300 ease-out ${
                  isSearchFocused ? "w-full max-w-[520px]" : isHomePage ? "w-full max-w-[220px]" : "w-full max-w-[250px]"
                }`}
                style={{ transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" }}
              >
                <div
                  className={`flex items-center rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
                    isSearchFocused
                      ? "border-[#406EFF]/25 bg-white/94 py-2.5 ring-4 ring-[#406EFF]/8 shadow-[0_14px_30px_rgba(64,110,255,0.12)] dark:bg-[#1e293b]/94 dark:border-[#406EFF]/40 dark:ring-[#406EFF]/20"
                      : isHomePage
                        ? "border-[#dbe4ff] bg-white/76 py-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)] hover:bg-white/92 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
                        : "border-white/70 bg-white/70 py-2.5 hover:bg-white/90 hover:shadow-[0_10px_26px_rgba(15,23,42,0.07)] dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
                  }`}
                  style={{ transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" }}
                >
                  <Search className="h-4 w-4 flex-shrink-0 text-[#86868b] dark:text-[#a1a1aa]" />
                  {isMobile ? (
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="ml-3 flex-1 bg-transparent text-base text-[#1d1d1f] dark:text-white outline-none placeholder:text-[#86868b] dark:placeholder:text-[#a1a1aa]"
                      placeholder="搜索文档..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onKeyDown={handleKeyDown}
                    />
                  ) : (
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="ml-3 min-w-0 flex-1 bg-transparent text-sm text-[#1d1d1f] dark:text-white outline-none placeholder:text-[#86868b] dark:placeholder:text-[#a1a1aa]"
                      placeholder={isSearchFocused ? "" : isHomePage ? "搜索文档与能力..." : "搜索文档..."}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                  {isLoading && <Loader2 className="ml-2 h-4 w-4 animate-spin flex-shrink-0 text-[#86868b] dark:text-[#a1a1aa]" />}
                  {!isLoading && !isSearchFocused && (
                    <kbd className="ml-2 flex-shrink-0 items-center gap-1 rounded-full border border-[#d1d1d6]/60 bg-white/80 dark:border-white/10 dark:bg-white/10 px-1.5 py-0.5 font-mono text-[10px] font-medium text-[#86868b] dark:text-[#a1a1aa]">
                      <span className="text-xs">⌘</span>K
                    </kbd>
                  )}
                </div>

                {showDropdown && (
                  <div
                    className={`absolute left-0 right-0 top-full mt-2 overflow-hidden rounded-[1.2rem] border border-white/70 bg-white dark:bg-[#0f172a] dark:border-white/10 shadow-[0_18px_40px_rgba(15,23,42,0.1)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.5)] transition-all duration-200 ${
                      isSearchFocused ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0"
                    }`}
                    style={{ transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" }}
                  >
                    {query.trim() === "" && recentSearches.length > 0 && (
                      <div className="border-b border-[#f5f5f7] dark:border-white/5 p-3">
                        <div className="mb-2 text-xs font-medium text-[#86868b] dark:text-[#a1a1aa]">最近搜索</div>
                        {recentSearches.map((search, i) => (
                          <button
                            key={i}
                            className="block w-full rounded-lg px-3 py-2 text-left text-sm text-[#1d1d1f] dark:text-[#f8fafc] transition-colors hover:bg-[#f5f5f7] dark:hover:bg-white/5"
                            onClick={() => setQuery(search)}
                          >
                            {search}
                          </button>
                        ))}
                      </div>
                    )}

                    {query.trim() !== "" && (
                      <>
                        <div className="border-b border-[#f5f5f7] dark:border-white/5 p-3">
                          <div className="flex flex-wrap gap-2">
                            {FILTER_TAGS.map((tag) => (
                              <button
                                key={tag.value}
                                disabled={isFilterPending}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                  activeFilter === tag.value
                                    ? "bg-[#406EFF] text-white"
                                    : "bg-[#f5f5f7] text-[#515154] hover:bg-[#e8e8ed] dark:bg-white/5 dark:text-[#a1a1aa] dark:hover:bg-white/10"
                                }`}
                                onClick={() => {
                                  setPendingFilter(tag.value);
                                  startFilterTransition(() => {
                                    setActiveFilter(activeFilter === tag.value ? null : tag.value);
                                  });
                                }}
                              >
                                {isFilterPending && pendingFilter === tag.value ? <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> : null}
                                {tag.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="max-h-[280px] overflow-y-auto">
                          {isLoading && results.length === 0 ? (
                            <div className="flex items-center justify-center gap-2 p-6 text-center text-sm text-[#86868b] dark:text-[#a1a1aa]">
                              <Loader2 className="h-4 w-4 animate-spin" /> 搜索中...
                            </div>
                          ) : results.length > 0 ? (
                            <div className="p-2">
                              {results.map((result, i) => (
                                <Link
                                  key={i}
                                  href={result.url}
                                  scroll={false}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    saveRecentSearch(query);
                                    navigateToSearchResult(result.url);
                                  }}
                                  className={`block rounded-xl p-3 transition-colors ${
                                    selectedIndex === i ? "bg-[#f5f5f7] dark:bg-white/10" : "hover:bg-[#f5f5f7]/60 dark:hover:bg-white/5"
                                  }`}
                                >
                                  <div className="mb-0.5 text-sm font-medium text-[#1d1d1f] dark:text-[#f8fafc]">{result.title}</div>
                                  <div className="line-clamp-1 text-xs text-[#86868b] dark:text-[#a1a1aa]">{result.excerpt}</div>
                                </Link>
                              ))}
                            </div>
                          ) : (
                            <div className="p-6 text-center text-sm text-[#86868b] dark:text-[#a1a1aa]">未找到相关结果</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {isSearchFocused && !isMobile && (
                <button
                  className="ml-3 flex-shrink-0 rounded-md bg-[#f5f5f7] dark:bg-white/10 px-2.5 py-1 text-xs font-medium text-[#86868b] dark:text-[#a1a1aa] transition-colors hover:text-[#1d1d1f] dark:hover:text-white"
                  onClick={handleCloseSearch}
                >
                  ESC
                </button>
              )}
            </div>

            <nav className="ml-auto flex flex-shrink-0 items-center gap-2 md:gap-3 lg:gap-4">
              <Link
                href="/docs/smart-ppt/guide/introduction"
                className={`rounded-full px-3.5 py-2 text-sm font-medium transition-all duration-200 ${
                  isHomePage
                    ? "border border-[#dbe4ff] bg-white/74 text-[#0f172a] shadow-[0_10px_20px_rgba(15,23,42,0.04)] hover:border-[#bfd0ff] hover:text-[#406EFF] dark:border-white/10 dark:bg-white/[0.05] dark:text-[#f8fafc] dark:hover:text-[#bfd0ff]"
                    : "rounded-xl text-[#515154] dark:text-[#a1a1aa] hover:bg-white/70 hover:text-[#1d1d1f] dark:hover:bg-white/10 dark:hover:text-white"
                }`}
              >
                开发文档
              </Link>
              <Link
                href="/playground"
                className={`rounded-xl px-3.5 py-2 text-sm font-medium transition-all duration-200 ${
                  pathname === "/playground"
                    ? "text-[#406EFF] dark:text-[#bfd0ff]"
                    : "rounded-xl text-[#515154] dark:text-[#a1a1aa] hover:bg-white/70 hover:text-[#406EFF] dark:hover:bg-white/10 dark:hover:text-white"
                }`}
              >
                API 调试台
              </Link>
              <Link
                href="/agent"
                className={`group hidden items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-semibold transition-all duration-200 md:flex ${
                  isAgentPage
                    ? "border-[#BCD0FF] bg-white text-[#2e5ae0] shadow-[0_12px_26px_rgba(64,110,255,0.14)]"
                    : isHomePage
                      ? "border-[#dbe4ff] bg-[#f6f8ff] text-[#406EFF] hover:border-[#bfd0ff] hover:bg-white hover:text-[#2e5ae0] dark:border-[#406EFF]/30 dark:bg-[#406EFF]/10 dark:hover:bg-[#406EFF]/20"
                      : "border-[#dbe4ff] bg-[#f6f8ff] text-[#406EFF] hover:border-[#bfd0ff] hover:bg-white hover:text-[#2e5ae0] dark:bg-[#406EFF]/10 dark:border-[#406EFF]/30 dark:hover:bg-[#406EFF]/20"
                }`}
              >
                <span
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full shadow-[0_4px_10px_rgba(51,90,255,0.12)] ${
                    isAgentPage ? "bg-[#406EFF] text-white" : "bg-white text-[#406EFF] dark:bg-transparent"
                  }`}
                >
                  <Sparkles className="h-3 w-3 transition-transform group-hover:scale-110" />
                </span>
                AI接入助手
              </Link>
              <div className={isHomePage ? "ml-1 hidden" : "hidden"}>
                <ThemeToggle />
              </div>
            </nav>
          </div>
        </div>
      </header>

      {isSearchFocused && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          onClick={handleCloseSearch}
          style={{ animation: "fadeIn 200ms ease-out" }}
        />
      )}

      {isMobile && isSearchFocused && (
        <div
          className="fixed inset-x-3 top-[5.5rem] z-40 overflow-hidden rounded-[1.25rem] border border-white/70 bg-white/95 shadow-[0_18px_40px_rgba(15,23,42,0.14)] backdrop-blur-xl sm:inset-x-4 sm:top-[5.75rem]"
          style={{ animation: "slideDown 300ms cubic-bezier(0.4, 0, 0.2, 1)" }}
        >
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              {FILTER_TAGS.map((tag) => (
                <button
                  key={tag.value}
                disabled={isFilterPending}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    activeFilter === tag.value
                      ? "bg-[#406EFF] text-white"
                      : "bg-[#f5f5f7] text-[#515154]"
                  }`}
                  onClick={() => {
                    setPendingFilter(tag.value);
                    startFilterTransition(() => {
                      setActiveFilter(activeFilter === tag.value ? null : tag.value);
                    });
                  }}
                >
                  {isFilterPending && pendingFilter === tag.value ? <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> : null}
                  {tag.label}
                </button>
              ))}
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              {isLoading && results.length === 0 ? (
                <div className="p-6 text-sm text-[#86868b] text-center flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> 搜索中...
                </div>
              ) : results.length > 0 ? (
                <div className="p-2">
                  {results.map((result, i) => (
                    <Link
                      key={i}
                      href={result.url}
                      scroll={false}
                      onClick={(e) => {
                        e.preventDefault();
                        saveRecentSearch(query);
                        navigateToSearchResult(result.url);
                      }}
                      className="block p-3 hover:bg-[#f5f5f7] rounded-xl transition-colors"
                    >
                      <div className="text-sm font-medium text-[#1d1d1f] mb-0.5">{result.title}</div>
                      <div className="text-xs text-[#86868b] line-clamp-1">{result.excerpt}</div>
                    </Link>
                  ))}
                </div>
              ) : query.trim() !== "" ? (
                <div className="p-6 text-sm text-[#86868b] text-center">未找到相关结果</div>
              ) : recentSearches.length > 0 ? (
                <div className="p-2">
                  <div className="text-xs font-medium text-[#86868b] mb-2 px-3">最近搜索</div>
                  {recentSearches.map((search, i) => (
                    <button
                      key={i}
                      className="block w-full text-left px-3 py-2 text-sm text-[#1d1d1f] hover:bg-[#f5f5f7] rounded-lg transition-colors"
                      onClick={() => setQuery(search)}
                    >
                      {search}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
